import { ref, computed } from 'vue'
import { useCluberLocalStorage } from '@cluber/tools-composables'
import { useApiAuthStoreRequest } from '@services/Auth/request'
import { type CustomAxiosRequestConfig } from '@services/api/declarations'
import { AxiosInstance } from 'axios'
import { type LocalStorageAuth } from '@services/Auth/declarations'
import { getAuthWorker } from '@core/workers/auth/authWorkerClient'
// ***
// DEFAULTS
// ***
type RefreshTokenStorage = LocalStorageAuth & {
  xAuthTokenApplied: boolean,
  keepLoggedIn: boolean,
  isRefreshTokenModalVisible: boolean,
}

const { storage } = useCluberLocalStorage<RefreshTokenStorage>({
  auth: {
    token: '',
    userIdentifier: '',
    refreshTokenValidity: undefined,
    tokenTtl: undefined,
  },
  xAuthTokenApplied: false,
  keepLoggedIn: false,
  isRefreshTokenModalVisible: false,
}, {
  initOnMounted: true,
  mergeDefaults: true,
})

const onAccept = ref<() => void>()
const onReject = ref<() => void>()
export const REFRESH_TOKEN_BUFFER_IN_MS = 1000 * 60 * Number(import.meta.env.VITE_REFRESH_TOKEN_BUFFER_IN_MINUTES)

export const expirationTokenBuffer = {
  ms: REFRESH_TOKEN_BUFFER_IN_MS,
  seconds: REFRESH_TOKEN_BUFFER_IN_MS / 1000,
}
const newToken = ref('')
const newTTLInSeconds = ref(0)
const keepLoggedInStatus = ref(storage.value.keepLoggedIn)
const failedRequests = ref<CustomAxiosRequestConfig[]>([])

// ***
// REFRESH TOKEN
// ***

export function useRefreshToken() {
  // ***
  // KEEP LOGGED IN
  // ***
  const userKeepSession = computed(() => storage.value.keepLoggedIn || keepLoggedInStatus.value)

  function toggleKeepLoggedIn() {
    keepLoggedInStatus.value = !keepLoggedInStatus.value
    storage.value.keepLoggedIn = keepLoggedInStatus.value
  }

  // ***
  // FAILED REQUESTS
  // ***

  function collectFailedRequest(request: CustomAxiosRequestConfig) {
    if (request.endpointKey !== 'postRefreshToken') {
      failedRequests.value.push(request)
    }
  }

  function retryFailedRequests(axiosInstance: AxiosInstance) {
    failedRequests.value.forEach((request) => {
      axiosInstance.request(request)
    })
    failedRequests.value = []
  }

  // ***
  // MODAL
  // ***

  function showModal() {
    storage.value.isRefreshTokenModalVisible = true
  }

  function hideModal() {
    storage.value.isRefreshTokenModalVisible = false
  }

  // ***
  // UPDATE TOKEN
  // ***

  function forceFailIfTokenRefreshFailed(config: CustomAxiosRequestConfig) {
    if (config.endpointKey === 'postRefreshToken') {
      throw new Error('Token refresh failed')
    }
  }

  // ***
  // SET/CHECK TOKEN VALIDITY
  // ***

  function calculateTokenValidity(ttlTime: number) {
    const TTL_TIME_IN_MS = ttlTime * 1000
    return Date.now() + TTL_TIME_IN_MS
  }

  function legacyRefreshTokenIsAboutToExpire() {
    const tokenExpirationTime = storage.value?.auth?.refreshTokenValidity

    if (tokenExpirationTime) {
      const currentTime = Date.now()
      const expirationThreshold = currentTime + expirationTokenBuffer.ms

      // TRUE if token expires within the next 10 minutes
      return tokenExpirationTime < expirationThreshold
    }
    return false
  }

  // ***
  // TOKEN REMAINING VALIDITY
  // ***

  const tokenRemainingValidity = computed(() => {
    const tokenValidity = storage.value?.auth?.refreshTokenValidity
    if (!tokenValidity) return expirationTokenBuffer.seconds

    const remainingTime = Math.max(0, (tokenValidity - Date.now()) / 1000)
    return Math.min(remainingTime, expirationTokenBuffer.seconds)
  })

  // ***
  // AUTH SHARED WORKER CHECK
  // ***

  function startWorkerTokenExpirationCheck() {
    getAuthWorker()?.postMessage({
      type: 'startTokenExpirationCheck',
    })
  }

  // ***
  // UPDATE TOKEN
  // ***

  function updateToken() {
    storage.value.auth.token = newToken.value
    storage.value.xAuthTokenApplied = false

    if (typeof newTTLInSeconds.value !== 'number' || Number.isNaN(newTTLInSeconds.value)) {
      return
    }

    storage.value.auth.refreshTokenValidity = calculateTokenValidity(newTTLInSeconds.value)
  }

  // ***
  // CHOOSE REFRESH TOKEN PROCESS
  // ***

  function chooseRefreshTokenProcess(requestToken: () => void) {
    if (legacyRefreshTokenIsAboutToExpire()) {
      if (userKeepSession.value) {
        requestToken()
      } else {
        showModal()
      }
    }
  }

  // ***
  // REFRESH TOKEN MODAL
  // ***

  function setRejectAndAcceptActions(
    reject: (error: Error) => void,
    requestToken: () => void,
  ) {
    onAccept.value = () => {
      requestToken()
      hideModal()
    }
    onReject.value = () => {
      reject(new Error('userDeniedRefreshToken'))
      hideModal()
    }
  }

  // ***
  // START REFRESH TOKEN PROCESS
  // ***
  let refreshTokenPromise: Promise<string> | null = null

  async function startRefreshTokenProcess() {
    if (refreshTokenPromise) {
      return refreshTokenPromise
    }

    const {
      fetchPostAuthRefreshToken,
      watchPostAuthRefreshToken,
    } = useApiAuthStoreRequest()

    refreshTokenPromise = new Promise<string>((resolve, reject) => {
      const { closeSubscription } = watchPostAuthRefreshToken({
        onSuccess: (data) => {
          if (data?.token) {
            newToken.value = data?.token
            newTTLInSeconds.value = data?.token_ttl
            updateToken()
            resolve(data?.token)
          } else {
            reject(new Error('noTokenReceived'))
          }
          closeSubscription()
        },
        onError: (error) => {
          reject(error)
          closeSubscription()
        },
      })

      // Set reject and accept actions for modal
      setRejectAndAcceptActions(reject, fetchPostAuthRefreshToken)

      // Choose refresh token process (Modal or Automatic)
      chooseRefreshTokenProcess(fetchPostAuthRefreshToken)
    }).finally(() => {
      refreshTokenPromise = null
    })

    return refreshTokenPromise
  }

  return {
    expirationTokenBuffer,
    isRefreshTokenModalVisible: computed(() => !!storage.value.isRefreshTokenModalVisible),
    userKeepSession,
    onAccept,
    onReject,
    tokenRemainingValidity,
    startWorkerTokenExpirationCheck,
    startRefreshTokenProcess,
    toggleKeepLoggedIn,
    forceFailIfTokenRefreshFailed,
    collectFailedRequest,
    retryFailedRequests,
    calculateTokenValidity,
    legacyRefreshTokenIsAboutToExpire,
    hideModal,
  }
}
